import type { MessageShareSnapshot } from "@/db/schema/chat";
import { ASPECT_RATIO_VALUE } from "@/lib/aspect-ratios";

/**
 * How far chat media may stray from square before it is cropped.
 *
 * Wide enough that the media shows at its own shape: a 9:16 phone video or
 * story, a 1.91:1 link-style landscape. The old 0.72 floor cut the top and
 * bottom off every portrait video. Shared by the renderers and
 * `chat-row-height.ts`, whose estimates must match the real boxes or the
 * thread jumps as rows mount.
 */
export const CHAT_MEDIA_MIN_ASPECT = ASPECT_RATIO_VALUE.story9By16;
export const CHAT_MEDIA_MAX_ASPECT = 1.91;

/** One height cap for photos and videos, so a 9:16 clip isn't a sliver. */
export const CHAT_SINGLE_MEDIA_MAX_WIDTH = 320;
export const CHAT_SINGLE_MEDIA_MAX_HEIGHT = 420;

export const clampChatMediaAspect = (ratio: number) =>
  Math.min(CHAT_MEDIA_MAX_ASPECT, Math.max(CHAT_MEDIA_MIN_ASPECT, ratio));

/**
 * The shape each shared entity's media is uploaded at, for snapshots saved
 * without dimensions. Guessing 16:9 for all of them cropped every portrait
 * event banner and story down to a letterbox.
 */
const SHARE_FALLBACK_ASPECT: Partial<
  Record<MessageShareSnapshot["entityType"], number>
> = {
  event: ASPECT_RATIO_VALUE.portrait4By5,
  event_booking_attendee: ASPECT_RATIO_VALUE.portrait4By5,
  post: ASPECT_RATIO_VALUE.portrait4By5,
  shop_item: ASPECT_RATIO_VALUE.portrait4By5,
  story: ASPECT_RATIO_VALUE.story9By16,
  venue: ASPECT_RATIO_VALUE.venueBooking,
  // `imageUrl` prefers the hiive's logo, which is square.
  hiive_membership: ASPECT_RATIO_VALUE.square,
};

/** The media box of a share card: stored dimensions, else the upload shape. */
export function shareCardMediaAspect(
  snapshot: Pick<MessageShareSnapshot, "entityType" | "media">,
): number {
  const media = snapshot.media;
  if (media?.width && media.height) {
    return clampChatMediaAspect(media.width / media.height);
  }
  return SHARE_FALLBACK_ASPECT[snapshot.entityType] ?? 16 / 9;
}
